import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * The per-board permission settings — the contract layer for **B38**,
 * `BE-38.5`.
 *
 * `FR-PERM-027` (who may add and remove this board's members — board admins
 * only, or board admins and normal members) and `FR-PERM-028` (whether
 * workspace members may edit a workspace-visible board without being added to
 * it). Both columns have existed since `db/postgres/migrations/0002_boards_
 * lists_cards.sql` and neither has ever been read by a route; `BE-38.5` is the
 * block that makes them live, and `0028_board_permission_defaults_and_invite_
 * link_activity.sql` is the migration that moved their defaults to where the
 * requirements had them all along.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `boardSchema` IS NOT WIDENED BY EITHER SETTING, AND THE PRECEDENT WAS
 * MEASURED RATHER THAN GUESSED
 * ---------------------------------------------------------------------------
 * Both settings must be readable by any board **reader**, not only by an admin:
 * `FR-PERM-025` says "non-admin members can see the current values but cannot
 * change them", and `00-`'s cross-cutting principle is that a gated capability
 * renders locked, never hidden. The obvious way to serve them is a field each
 * on `boardSchema`, and it is the wrong way.
 *
 * **B16 answered this question for the sibling setting and the answer is
 * unambiguous.** `boards.commenting_permission` is `FR-COLLAB-014`'s per-board
 * setting, in every structural respect the same kind of thing as these two, and
 * `commentingPermission` is **deliberately absent from `boardSchema`** — the
 * argument is at `packages/shared/src/comment.ts`'s `boardCommentingSchema`,
 * and its core is that widening `boardSchema` changes **eight** shipped
 * responses (`GET /boards/:id`, both short-link resolvers,
 * `GET /workspaces/:id/boards`, the title patch, the visibility patch,
 * close/reopen and `boardContentsSchema.board`) to add a field none of them
 * asked for. What B16 shipped instead is a **narrow companion schema plus its
 * own GET**: `boardCommentingSchema = { boardId, commentingPermission }`,
 * served by `GET /boards/:id/commenting-permission` and returned by the PATCH
 * beside it.
 *
 * This file mirrors that exactly, once per setting — `boardMemberPermission
 * Schema` and `boardSelfJoinSchema`, each `{ boardId, <the one field> }`, each
 * the response of a `GET` and of a field-scoped `PATCH` on the same path. It is
 * `CLAUDE.md`'s "extend a shipped schema on a copy, never widen it in place"
 * applied to the case the rule was written for, and it is B16's own precedent
 * rather than a fresh judgement, which matters: three per-board settings that
 * each answered this question differently would be three different client
 * idioms for one kind of fact.
 *
 * ⚠ **Two companion schemas, not one `{ boardId, memberAddRemovePermission,
 * workspaceSelfJoinEnabled }`.** SC-4 is one route per *field* and these are
 * two fields with two PATCHes; a shared response shape would mean each PATCH
 * echoing back a value it did not touch, and a client caching the pair would
 * have no way to tell which half of a stale object it may trust. It would also
 * make `FR-PERM-028` AC3's refusal (`409` on a private board — see
 * `boardSelfJoinPatchSchema`) look as though it could apply to the other
 * setting, which it cannot.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------------------------------------------------------
 *   - **`commentingPermission`.** `FR-PERM-026` is **reconciled, not
 *     reimplemented**: `PATCH /boards/:id/commenting-permission` already exists
 *     (B16, `apps/server/src/routes/comments.ts`) and its five values are
 *     exactly `FR-PERM-026`'s five, "any user" included with its
 *     public-boards-only rule enforced at both the setting and the guard. B38
 *     writes no code for it and this file declares no shape for it — a second
 *     schema here would be the duplication the item exists to prevent. The
 *     `setting` union below names `'commenting'` because the *event* vocabulary
 *     spans all three; the *contract* for that setting stays in `./comment`,
 *     where its route is.
 *   - **A unified `PUT /boards/:id/settings` and any body carrying more than
 *     one setting.** SC-4 again, and the objection `boardTitlePatchSchema`
 *     raises at length: an absent key and a cleared value are the same thing in
 *     the body a PUT receives, and `FR-COLLAB-026`'s two-members-at-once case
 *     becomes inexpressible.
 *   - **`FR-PERM-017`'s workspace-admin cascade.** `FR-PERM-027`'s own edge
 *     case says workspace admins retain member management regardless of this
 *     setting, and that edge case cites `FR-PERM-017`, which is **`BE-39.3`'s**
 *     and Phase 2. An edge case sitting inside this block's requirement does not
 *     transfer ownership of the requirement it cites. Nothing here expresses the
 *     cascade and `requireBoardMemberManager` says in capitals not to widen it
 *     early. **Recorded as owed, not built.**
 *   - **Anything about who may *see* a board.** That is `boards.visibility`,
 *     `FR-PERM-008`–`FR-PERM-012`, and it lives on `boardSchema` itself because
 *     B09 put it there for reasons that file states. These two settings govern
 *     *what a person who can already see the board may do*, which is a
 *     different question and deliberately a different shape.
 *
 * This file declares no column width constant. Both columns are CHECK-bounded
 * vocabularies or a boolean, so there is no width to state — `./cross-board-
 * move`'s and `./board-archive`'s footing, reached for the opposite reason
 * (they inherit every bound; this file has no string field at all).
 */

// ---------------------------------------------------------------------------
// The `setting` discriminator — FR-PERM-025 AC4, and `board.permission_changed`
// in `./events`.
// ---------------------------------------------------------------------------

/**
 * **Every value `board.permission_changed`'s payload may carry as `setting`.**
 *
 * ⚠⚠ **THIS UNION IS MINTED HERE, BY B38, AND IT MUST NEVER BE DECLARED
 * TWICE.** `./events`' block comment (f) fixed the contract at B16 and named
 * the block that would extend it: "**B16 is the first block to emit it, so B16
 * fixes the payload contract: `setting` is REQUIRED on every
 * `board.permission_changed` payload** (`'commenting'` here), and `B38` extends
 * the union of its values rather than inventing a name." B38 is that block.
 *
 * ⚠ **B16 extended nothing, because B16 declared nothing to extend.** Measured
 * before this file was written: `board.permission_changed`'s payload is
 * `z.record(z.string(), z.unknown())` at the type level (`./events` binds no
 * payload shape to any name, deliberately), and `'commenting'` is a **bare
 * string literal at B16's emit site** in `apps/server/src/routes/comments.ts`.
 * So "extend the declaration B16 made" resolves to "make the declaration B16
 * described", and the emit site is edited to import this tuple rather than to
 * spell its own literal a second time. **Do not leave three string literals
 * scattered across two route files**: the whole value of a discriminator is
 * that `B28` can switch on it exhaustively, and a fourth misspelt literal would
 * write a row that renders as a setting change nobody can name, with nothing
 * red anywhere — `activity_entries` is append-only and there is no migration to
 * fix a payload in.
 *
 * ---------------------------------------------------------------------------
 * WHY IT LIVES IN THIS FILE AND NOT IN `./events` OR `./comment`
 * ---------------------------------------------------------------------------
 * Three candidates, and the other two are worse for stated reasons:
 *
 *   - **`./events`** is where the name `board.permission_changed` is
 *     registered, which makes it the intuitive home — and that file binds **no
 *     payload shape to any name at the type level**, on purpose, so this tuple
 *     would be the first payload-level declaration in a registry whose entire
 *     discipline is that payloads are documented in docstrings and enforced at
 *     write sites. Introducing one exception there would invite a second.
 *   - **`./comment`** owns `COMMENTING_PERMISSIONS`, whose pattern this tuple
 *     follows, and owns one of the three values. Declaring the union there
 *     would make the file that owns **one** member own the whole vocabulary,
 *     and would invert the import direction: `./comment` would then be imported
 *     by every future block that adds a board setting, for a reason that has
 *     nothing to do with comments.
 *   - **Here**, because this file is the one whose subject is *a board's
 *     permission settings* as a family. It holds two of the three members
 *     outright, and the third is named rather than defined — which is the same
 *     relationship `BOARD_VISIBILITIES` has to `enterprise_visible`: a
 *     vocabulary states every value the fact can take, including the ones whose
 *     behaviour is somebody else's.
 *
 * ---------------------------------------------------------------------------
 * THE VALUES, AND THE SPELLING RULE
 * ---------------------------------------------------------------------------
 * Each value names the **setting**, not the column and not the route:
 *
 *   - `'commenting'` — `boards.commenting_permission`, `FR-COLLAB-014` /
 *     `FR-PERM-026`, B16's, emitted from `PATCH /boards/:id/commenting-
 *     permission`. Spelt as B16 spelt it; renaming it now would orphan every
 *     row written since B16 on an append-only table.
 *   - `'member_add_remove'` — `boards.member_add_remove_permission`,
 *     `FR-PERM-027`, emitted from `PATCH /boards/:id/member-permission`.
 *   - `'workspace_self_join'` — `boards.workspace_self_join_enabled`,
 *     `FR-PERM-028`, emitted from `PATCH /boards/:id/self-join`.
 *
 * ⚠ **`'visibility'` is NOT a member, and its absence is the load-bearing one.**
 * A board's visibility has a **name of its own** in the event vocabulary —
 * `board.visibility_changed`, a `0004` placeholder B09 claimed — so a
 * visibility change emits that name and never this one. Adding `'visibility'`
 * here would create a second, equally valid way to record one fact, and a feed
 * would then hold two spellings of "Ada made this board public" that no
 * consumer could merge. `./events`' argument for *why* the generic name plus a
 * discriminator beat `board.commenting_permission_changed` cuts the other way
 * here and gives the same answer: one fact, one name.
 *
 * ⚠ **`ck_activity_entries_type` does not police this tuple.** The CHECK
 * constrains `activity_entries.type`, which is `board.permission_changed` for
 * all three; `setting` rides in a JSONB payload the database does not read. So
 * a value added here needs **no migration** — and, symmetrically, a typo here
 * is caught by nothing but a test.
 */
export const BOARD_PERMISSION_SETTINGS = [
  'commenting',
  'member_add_remove',
  'workspace_self_join',
  'voting_enabled',
  'voting_permission',
  'voting_tallies',
] as const;

export const boardPermissionSettingSchema = z.enum(BOARD_PERMISSION_SETTINGS);

export type BoardPermissionSetting = z.infer<typeof boardPermissionSettingSchema>;

// ---------------------------------------------------------------------------
// FR-PERM-027 — who may add and remove this board's members.
// ---------------------------------------------------------------------------

/**
 * `boards.member_add_remove_permission varchar(32) NOT NULL`,
 * `ck_boards_member_add_remove_permission CHECK (member_add_remove_permission
 * IN ('admins', 'all_members'))`.
 *
 * `FR-PERM-027`'s two options, in the requirement's own order: "**admins** (only
 * board admins may) … or **all members** (board admins and normal members may —
 * **the default**)". Keeping this array in the constraint's order is
 * `LABEL_COLOURS`' and `COMMENTING_PERMISSIONS`' stated rule — a drift between
 * the two lists then shows up in a diff rather than only in a failing INSERT.
 *
 * ⚠⚠ **"the default" in that sentence is `'all_members'`, and until `0028` the
 * column defaulted to `'admins'`.** Both engines' `0002` wrote the narrower
 * value with no comment justifying it, unlike almost every other line in that
 * file, while the shipped guard (`requireBoardMemberManager`) admitted
 * `'normal'` unconditionally and said in its own docstring that
 * `FR-PERM-027`'s default is "all members". The column and the code disagreed
 * from B10 to B38 and only the column was unread, so only the column was wrong.
 * `0028` moves the default and backfills the 12,106 existing rows, so that
 * making this column live is **monotone**: no board changes standing on the day
 * `BE-38.5` lands. Reading it at the old default would have been a silent mass
 * narrowing with no migration, no event and no status code — every board would
 * simply have stopped letting normal members add anybody, and a perfectly
 * ordinary 403 would have been the only symptom.
 *
 * ⚠ **Neither value admits an observer, under either setting.**
 * `FR-PERM-027` is unconditional — "observers can never add or remove members" —
 * so `'all_members'` means *admins and normal members*, never *everybody on the
 * member list*. The vocabulary cannot express that on its own; the guard does
 * (`mayManageBoardMembers` composes two allow-list arms, never a negation, so a
 * fourth `ck_board_members_role` value could not inherit the widest rights by
 * accident). Said here because "all_members" is the value a reader will misread.
 *
 * ⚠ **The setting also governs who may generate an invite link**
 * (`FR-PERM-027`, "The setting also governs who can generate invite links"), so
 * `./board-invite-link`'s three management routes sit behind the same predicate
 * as `POST /boards/:id/members` and `DELETE /boards/:id/members/:userId`. Three
 * routes, one predicate, no second copy.
 */
export const MEMBER_ADD_REMOVE_PERMISSIONS = ['admins', 'all_members'] as const;

/**
 * `z.enum` over the vocabulary, used on the **view** as well as on the input —
 * `commentingPermissionSchema`'s call, for its stated reason. The distinction
 * that decides it is whether an out-of-vocabulary value is *stale data a reader
 * should tolerate* or *inexpressible in the column*: a third value here cannot
 * exist, because `ck_boards_member_add_remove_permission` refuses it at INSERT.
 * So a value that fails to parse means the constraint and this tuple have
 * drifted, which a read should surface rather than pass through.
 *
 * Unlike `boardVisibilitySchema` there is **no asymmetry between the view and
 * the patch**: the CHECK admits two values and both are settable, so
 * `boardMemberPermissionPatchSchema` below reuses this schema whole. That makes
 * the integration suite's comparison against the live constraint a meaningful
 * check rather than a tautology — `commentingPermissionSchema` makes the
 * identical observation about its own five.
 */
export const memberAddRemovePermissionSchema = z.enum(MEMBER_ADD_REMOVE_PERMISSIONS);

export type MemberAddRemovePermission = z.infer<typeof memberAddRemovePermissionSchema>;

/**
 * `PATCH /boards/:id/member-permission` — `FR-PERM-027`. SC-4, field-scoped, and
 * the same "never a `PUT /boards/:id`" argument `boardTitlePatchSchema` gives at
 * length.
 *
 * `requireBoardAdminRole` guards it — the **literal** `board_members.role =
 * 'admin'`, matching `PATCH /boards/:id/visibility`. ⚠ Not `requireBoardAdmin`:
 * the two names are one word apart and pick different people, and the wider one
 * would fold in `FR-PERM-017`'s workspace-admin cascade, which is `BE-39.3`'s.
 *
 * ⚠ **Setting the value the board already has emits NOTHING and bumps no
 * timestamp.** B16's documented no-op, inherited rather than re-decided:
 * `board.permission_changed`'s docstring in `./events` argues it in full, and
 * this is the *toggle* side of B14's boundary — two clients can fire a settings
 * patch at once — rather than `comment.edited`'s explicit-Save side. The route
 * expresses it as a conditional `UPDATE … WHERE id = :id AND <column> <> :next`
 * with `.returning()` used only as the *did it match* test. A schema cannot
 * express that, which is exactly why it is written here: the shape is
 * indistinguishable between the two behaviours.
 */
export const boardMemberPermissionPatchSchema = z.strictObject({
  memberAddRemovePermission: memberAddRemovePermissionSchema.describe(
    'Who may add and remove this board’s members (FR-PERM-027). “admins” — ' +
      'board admins only. “all_members” — board admins and normal members; it ' +
      'does NOT include observers, who can never add or remove members under ' +
      'either setting. The setting also governs who may generate the board’s ' +
      'invite link. Workspace admins are not covered either way: FR-PERM-017 ' +
      'is BE-39.3’s and is not built.',
  ),
});

export type BoardMemberPermissionPatch = z.infer<typeof boardMemberPermissionPatchSchema>;

/**
 * What `GET /boards/:id/member-permission` and
 * `PATCH /boards/:id/member-permission` both answer with —
 * `boardCommentingSchema`'s shape, one setting over.
 *
 * Two keys and no third. **No `mayManage`**, and that omission is the same one
 * B16 made when it kept `mayComment` off `boardCommentingSchema`: a per-viewer
 * field on a per-board response is how two clients holding one cached object
 * start disagreeing about what it means. The caller's own standing is decided
 * by the routes that need it — a caller who may read but may not change this
 * setting gets a **403** from the PATCH, never a 404, precisely so `FE-38.5`
 * can render the control **locked rather than hidden** (`00-`'s cross-cutting
 * principle, and `FR-PERM-025`: "non-admin members can see the current values
 * but cannot change them").
 *
 * The `GET` is `requireBoardReader`; the `PATCH` is `requireBoardAdminRole`.
 * That asymmetry is the requirement, not an oversight: the values are visible
 * to everybody who can see the board and settable by board admins alone.
 */
export const boardMemberPermissionSchema = z.object({
  boardId: ulidSchema,
  memberAddRemovePermission: memberAddRemovePermissionSchema,
});

export type BoardMemberPermission = z.infer<typeof boardMemberPermissionSchema>;

// ---------------------------------------------------------------------------
// FR-PERM-028 — may workspace members edit this board without being added to it?
// ---------------------------------------------------------------------------

/**
 * `PATCH /boards/:id/self-join` — `FR-PERM-028`. SC-4, field-scoped.
 *
 * `boards.workspace_self_join_enabled boolean NOT NULL`. **A boolean and not a
 * vocabulary**, because the requirement is a toggle with two states and there is
 * no third: "workspace members may edit this board without being added to it",
 * on or off. A future third state ("ask an admin") would be a different column
 * and a different requirement, not a widening of this one.
 *
 * ⚠⚠ **`FR-PERM-028` says the toggle "is on by default" and the column
 * defaulted to `false` until `0028`.** Same shape as `FR-PERM-027`'s default
 * above, same unargued transcription in `0002`, same resolution: `0028` moves
 * the default to `true` and backfills, because the shipped `requireBoardWriter`
 * has admitted any caller with a non-null `workspaceRole` since B09 — which
 * *is* self-join-enabled behaviour — so reading the column at its old default
 * would have stopped every workspace member editing every workspace-visible
 * board in the product, silently, in the same breath as making the setting
 * configurable.
 *
 * ⚠ **`409` on a private board, and the ordering is contract.**
 * `FR-PERM-028` AC3: the toggle is "only offered when the board is
 * workspace-visible" — a board nobody outside the member list can *see* has no
 * population for self-join to admit, so enabling it there would store a value
 * with no meaning. That refusal is a fact about the board's current
 * **visibility**, which is what a 409 means in this codebase
 * (`assertBoardIsOpen`, `assertBoardRetainsAdmin`) and what the caller can fix
 * by changing a different setting — never a 422, which would say the body was
 * malformed when it was not. Refusal order is **404, then 403, then 409**, so a
 * stranger still gets 404 and a non-admin member still gets 403 before the
 * board's state is ever consulted. `commentingPermissionPatchSchema` makes the
 * identical call for `any_user` on a non-public board.
 *
 * ⚠ **Turning the toggle off does not remove anybody.** A workspace member who
 * has already been added to the board holds a `board_members` row, and
 * `FR-PERM-028` AC4 keeps their edit rights; the guard tests
 * `boardRole === null` rather than `workspaceRole !== null` for exactly that
 * reason. Nothing in this body cascades, and no membership is written or
 * deleted by it.
 */
export const boardSelfJoinPatchSchema = z.strictObject({
  workspaceSelfJoinEnabled: z
    .boolean()
    .describe(
      'Whether members of this board’s workspace may edit it without being ' +
        'added to its member list (FR-PERM-028). On by default. Turning it off ' +
        'leaves existing board members untouched — they keep editing through ' +
        'their own membership (AC4). Refused with a 409 on a private board: the ' +
        'toggle is only meaningful where workspace members can see the board ' +
        '(AC3).',
    ),
});

export type BoardSelfJoinPatch = z.infer<typeof boardSelfJoinPatchSchema>;

/**
 * What `GET /boards/:id/self-join` and `PATCH /boards/:id/self-join` both answer
 * with — `boardMemberPermissionSchema`'s shape and, one file over,
 * `boardCommentingSchema`'s.
 *
 * ⚠ **No `mayJoin` and no `joined`.** Both would be per-viewer facts on a
 * per-board response, which is the field `boardMemberPermissionSchema` above
 * refuses for B16's stated reason. Whether *this* caller may self-join is
 * answered by `POST /boards/:id/join` itself, which is idempotent for somebody
 * already on the member list and refuses everybody else with the ordinary
 * 404/403 pair — a question the client asks by acting rather than by reading a
 * boolean that was true when the response was cached.
 *
 * ⚠ **No `visibility` either**, though `FR-PERM-028` AC3 makes the toggle
 * meaningless on a private board and a client rendering the control needs to
 * know. It is already on `boardSchema`, which every client holding a board has
 * fetched, and restating it here would be a second copy of a value the PATCH
 * beside it can change — two snapshots of one fact in one response is where a
 * client renders a control against a visibility the board no longer has.
 */
export const boardSelfJoinSchema = z.object({
  boardId: ulidSchema,
  workspaceSelfJoinEnabled: z.boolean(),
});

export type BoardSelfJoin = z.infer<typeof boardSelfJoinSchema>;
